/* ZSportsTech — section-batfit.js · AI Bat Fit (Zac.ai)
   A 13-question instrument. LEFT (white) shows one question at a time; RIGHT
   (navy) draws the player's performance map live — a 6-axis radar that gains a
   point per answer, a gender-driven figure, an emerging archetype, and a
   running log. The Result resolves the map into a grade + model and deep-links
   to the PDP (?model=). Vanilla, namespaced, reduced-motion aware, accessible.
   No Liquid — Claude Code wires the data later.
   ─────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var root = document.getElementById('bf');
  if (!root) return;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Exit target ───────────────────────────────────
     Arriving from a PDP's "Start the AI Bat Fit" CTA carries ?return=<path>.
     Exit then goes back to that product instead of home, so leaving the quiz
     returns you where you were. The value comes off the URL, so it is only
     honoured when it is one of our own product paths — never an absolute or
     protocol-relative URL, which would make Exit an open redirect. */
  (function () {
    var exit = root.querySelector('.bf__exit');
    if (!exit) return;
    var back;
    try {
      back = new URLSearchParams(window.location.search).get('return');
    } catch (e) {
      return;
    }
    if (!back || back.indexOf('..') !== -1) return;          /* no climbing out of /products/ */
    if (!/^\/products\/[A-Za-z0-9._~\-\/]+$/.test(back)) return;
    exit.setAttribute('href', back);
    exit.setAttribute('aria-label', 'Exit the Bat Fit and go back to the product');
  }());

  var AXES = ['power', 'pickup', 'timing', 'balance', 'control', 'aggression'];

  /* ── DYNAMIC: questions (id, type, q, why, options w/ score deltas) ───── */
  var QUESTIONS = [
    { id: 'gender', type: 'tiles', cols: 2, q: 'Select your gender', why: 'Sets the figure on your performance map.', options: [
      { v: 'male', label: 'Male' }, { v: 'female', label: 'Female' } ] },
    { id: 'age', type: 'slider', q: 'How old are you?', why: 'Age shapes how much bat you can carry through an innings.', min: 8, max: 70, step: 1, def: 22, unit: 'years',
      score: function (n) { return n < 18 ? { pickup: 18, aggression: 8 } : n <= 30 ? { power: 12, balance: 10 } : { control: 16, timing: 8 }; } },
    { id: 'height', type: 'slider', q: 'How tall are you?', why: 'Height drives leverage and the size that fits your stance.', min: 48, max: 82, step: 1, def: 69, unit: '',
      /* value is total inches; displayed as feet & inches, with centimetres
         alongside for anyone who does not think in feet. The cm rides in the
         readout's unit slot — already styled small and muted — so the big
         number stays feet/inches and the metric reading sits beside it rather
         than competing with it. The slider's own value stays in inches: the
         ZAC height bracket is derived from it, so converting the stored value
         would move every bracket boundary. */
      fmt: function (n) { return Math.floor(n / 12) + '′' + (n % 12) + '″'; },
      unitFor: function (n) { return Math.round(n * 2.54) + ' cm'; },
      score: function (n) { return n >= 72 ? { power: 16, balance: 8 } : n >= 65 ? { balance: 14, timing: 6 } : { pickup: 16, timing: 6 }; } },
    { id: 'weight', type: 'slider', q: 'And your weight?', why: 'Mass behind the shot: it tunes your power band.', min: 20, max: 130, step: 1, def: 70, unit: 'kg',
      score: function (n) { return n >= 85 ? { power: 18, aggression: 8 } : n >= 60 ? { balance: 12, power: 8 } : { pickup: 16, timing: 6 }; } },
    { id: 'bat', type: 'tiles', cols: 2, q: 'Which bat are you looking for?', why: 'Ball type frames the whole match.', options: [
      { v: 'leather', label: 'Leather Ball Bat', s: { power: 14, control: 10 } },
      /* No tennis bats in the range yet — shown but not selectable, so the
         question still reads as a real choice rather than silently dropping to
         one option. Re-enable by deleting `soon: true`. */
      { v: 'tennis', label: 'Tennis Cricket Bat', soon: true, s: { aggression: 14, pickup: 8 } } ] },
    { id: 'position', type: 'options', q: 'Select your batting position', why: 'Your role decides how the bat needs to behave.', options: [
      { v: 'p12', label: '1 & 2', sub: 'See off the new ball', s: { control: 18, timing: 12 } },
      { v: 'p34', label: '3 & 4', sub: 'Build the innings', s: { timing: 16, balance: 8 } },
      { v: 'p567', label: '5, 6 & 7', sub: 'Rotate & accelerate', s: { balance: 14, power: 10, aggression: 6 } },
      { v: 'p8', label: '8 and onward', sub: 'Swing freely', s: { aggression: 12, power: 8 } } ] },
    { id: 'foot', type: 'options', skipForKids: true, q: 'Prefer playing a good length ball on', why: 'How you meet the good length ball hints at balance through the shot.', options: [
      { v: 'front', label: 'Front Foot', sub: 'Drives & lofts', s: { control: 14, timing: 8 } },
      { v: 'back', label: 'Back Foot', sub: 'Cuts & pulls', s: { power: 14, aggression: 8 } },
      { v: 'both', label: 'Both', sub: 'Equally at home', s: { balance: 18, control: 8 } } ] },
    { id: 'scoring', type: 'options', skipForKids: true, q: 'Scoring more runs on', why: 'Your scoring zone shapes the pickup and profile.', options: [
      { v: 'off', label: 'Off Side', sub: 'Cover & point', s: { control: 16, timing: 10 } },
      { v: 'on', label: 'On Side', sub: 'Flick & pull', s: { power: 14, aggression: 8 } },
      { v: 'both', label: 'Both', sub: 'Score all around', s: { balance: 16, control: 8 } } ] },
    { id: 'nature', type: 'options', skipForKids: true, q: 'Select your nature of playing', why: 'Tempo decides how much aggression the bat must reward.', options: [
      { v: 'defensive', label: 'Defensive', sub: 'Occupy the crease', s: { control: 20, timing: 10 } },
      { v: 'slog', label: 'Stroke / Slog', sub: 'Take the attack on', s: { aggression: 22, power: 12 } },
      { v: 'both', label: 'Both', sub: 'Bat the situation', s: { balance: 18, timing: 6 } } ] },
    { id: 'build', type: 'tiers', q: 'Your body type', why: 'Build feeds straight into your power band.', options: [
      { v: 'skinny', label: 'Skinny Build', h: 40, w: 10, s: { pickup: 16, timing: 8 } },
      { v: 'average', label: 'Average Build', h: 68, w: 15, s: { balance: 16, timing: 8 } },
      { v: 'heavy', label: 'Heavy Build', h: 100, w: 21, s: { power: 20, aggression: 10 } } ] },
    { id: 'experience', type: 'path', q: 'Experience level', why: 'Experience calibrates the control we expect you to want.', options: [
      { v: 'beginner', label: 'Beginner', s: { control: 8, balance: 8 } },
      { v: 'intermediate', label: 'Intermediate', s: { control: 12, timing: 8 } },
      { v: 'club', label: 'Club', s: { timing: 14, control: 10 } },
      { v: 'professional', label: 'Professional', s: { control: 16, timing: 12, balance: 8 } } ] },
    { id: 'pitch', type: 'options', q: 'Pitch condition', why: 'The surface decides the response you need.', options: [
      { v: 'high', label: 'High Bounce', sub: 'Pace on the ball', s: { pickup: 14, power: 10 } },
      { v: 'standard', label: 'Standard Bounce', sub: 'Indian & Asian conditions', s: { balance: 14, timing: 6 } } ] },
    { id: 'details', type: 'fields', q: 'Where do we send your match?', why: 'Your map is ready; this is just where it lands.', fields: [
      { k: 'name', placeholder: 'Full name', type: 'text' },
      { k: 'email', placeholder: 'Email address', type: 'email' },
      { k: 'phone', placeholder: 'WhatsApp number', type: 'tel', dial: true } ] }
  ];

  /* ── Under 5 feet: drop the three shot-preference questions ──────────────
     A player under 5'0" is a child, and asking one to characterise the foot
     they meet a good length ball on, the side they score through, and their
     tempo yields answers the sizing model cannot use — junior sizing is
     driven by height, weight and build. So those three are skipped rather
     than collected and ignored.

     They are NOT left blank, though. Each is auto-selected to its 'Both'
     option, so /submit-profile carries good_length_ball / scoring_runs_on /
     nature_of_playing as "Both" rather than empty strings — the backend
     receives a complete profile and never has to reason about a missing
     field. 'Both' is the honest answer as well as the safe one: it is the
     neutral choice on all three, so it commits a child to neither front nor
     back foot, neither side, neither tempo.

     Nothing else changes: the weight buckets, every other question, and the
     shape of the ZAC payload stay as they are. When the API learns to size
     juniors, deleting the three skipForKids flags restores the full run with
     no other edit. */
  var KID_HEIGHT_INCHES = 60;   /* 5'0", matching the height slider's inches */
  var KID_AUTO_VALUE = 'both';  /* every skipForKids question offers this */
  function isKid() {
    var h = state.answers.height;
    return !!h && Number(h.value) < KID_HEIGHT_INCHES;
  }
  /* Keeps the three skipped answers in step with the height slider. Called
     after every answer, because Back lets a player cross 5' mid-run.
     `auto` marks an answer this filled in, so crossing back over 5' can tell
     an auto-fill from a real choice: the auto one is withdrawn and the
     question asked properly, while a real choice made earlier in a taller
     run is stashed and handed back rather than silently lost. */
  var displacedAnswers = {};
  function syncKidAnswers() {
    var kid = isKid();
    QUESTIONS.forEach(function (q) {
      if (!q.skipForKids) return;
      var cur = state.answers[q.id];
      if (kid) {
        var o = findOpt(q, KID_AUTO_VALUE);
        if (!o) return;                                   /* no 'both' — leave alone */
        if (cur && !cur.auto) displacedAnswers[q.id] = cur;
        state.answers[q.id] = { value: o.v, label: o.label, score: o.s || null, auto: true };
      } else if (cur && cur.auto) {
        if (displacedAnswers[q.id]) { state.answers[q.id] = displacedAnswers[q.id]; delete displacedAnswers[q.id]; }
        else delete state.answers[q.id];
      }
    });
  }
  /* The live question list. Recomputed on every read rather than cached,
     because Back lets the player return to the height slider and change which
     side of 5' they are on mid-run. */
  function activeQuestions() {
    if (!isKid()) return QUESTIONS;
    return QUESTIONS.filter(function (q) { return !q.skipForKids; });
  }
  /* The list includes the trailing 'details' contact-info step, which isn't a
     fit question — displayed progress/counts read out of the scored ones. */
  function scoredCount() { return activeQuestions().length - 1; }

  /* ── ZAC bracket grouping ────────────────────────────────────────────────
     The backend profile schema asks height as brackets; our quiz keeps an
     open slider. This helper groups the slider value into the backend's
     exact bracket strings for the quiz → ZAC handoff. Weight goes through
     as the raw kg number — the backend expects a number, not a bracket. */
  function zacHeightBracket(inches) {
    /* quiz stores height as total inches; ZAC brackets are feet/inches */
    if (inches < 50) return 'Upto - 4\'2"';
    if (inches < 53) return '4\'2" - 4\'5"';
    if (inches < 56) return '4\'5" - 4\'8"';
    if (inches < 59) return '4\'8" - 4\'11"';
    if (inches < 61) return '4\'11" - 5\'1"';
    if (inches < 64) return '5\'1" - 5\'4"';
    if (inches < 66) return '5\'4" - 5\'6"';
    if (inches < 74) return '5\'6" - 6\'2"';
    return '6\'2" and above';
  }
  window.ZBAT_ZAC_BRACKETS = { height: zacHeightBracket };

  /* Quiz answer values → the backend schema's exact option strings.
     UI labels stay free to read well on screen; only the payload maps. */
  var ZAC_VALUE_MAP = {
    bat: { leather: 'Leather ball bat', tennis: 'Tennis cricket bat' },
    position: { p12: '1 & 2', p34: '3 & 4', p567: '5,6 & 7', p8: '8 and onward' },
    foot: { front: 'Front foot', back: 'Back Foot', both: 'Both' },
    scoring: { off: 'Off side', on: 'On side', both: 'Both' },
    nature: { defensive: 'Defensive', slog: 'Stroke/Slog', both: 'Both' },
    build: { skinny: 'Lean', average: 'Medium Build', heavy: 'Heavy Build' }
  };

  /* ── Quiz → ZAC handoff (API 2: /submit-profile) ─────────────────────────
     /submit-profile must carry the chat_id returned by /chat, so when no
     conversation exists yet we start one first, then push the answers.
     Failures are non-blocking — the user always reaches the chat. */
  function buildProfileAnswers() {
    /* keys are the canonical field names from the backend's profile_questions
       event — see the /chat API contract */
    function ans(id) { var a = state.answers[id]; return a ? a.label : ''; }
    function raw(id) { var a = state.answers[id]; return a ? a.value : null; }
    function mapped(id) { var m = ZAC_VALUE_MAP[id], v = raw(id); return (m && m[v]) || ans(id); }
    var d = raw('details') || {};
    return {
      name: d.name || '',
      age: String(raw('age') || ''),
      height: zacHeightBracket(raw('height')),
      weight: String(raw('weight') || ''),
      gender: ans('gender'),
      bat_type: mapped('bat'),
      batting_position: mapped('position'),
      good_length_ball: mapped('foot'),
      scoring_runs_on: mapped('scoring'),
      nature_of_playing: mapped('nature'),
      body_type: mapped('build'),
      experience_level: ans('experience'),
      pitch_condition: ans('pitch'),
      email: d.email || '',
      phone: d.phone || ''
    };
  }
  /* Start the ZAC conversation early (while the user types their details /
     watches the result animation) so the slow streaming /chat call is done
     before "Reveal your match" — leaving only the fast /submit-profile. */
  var zacWarmUp = null;
  function warmUpZac() {
    var api = window.ZacAPI;
    if (zacWarmUp || !api) return zacWarmUp;
    zacWarmUp = api.getChatId()
      ? Promise.resolve()
      : api.chat('Hi Zac — I just completed the AI Bat Fit quiz on the website.').catch(function () { zacWarmUp = null; });
    return zacWarmUp;
  }
  function submitProfileToZac() {
    var api = window.ZacAPI;
    if (!api) return Promise.reject({ message: 'ZacAPI unavailable on this page' });
    var answers = buildProfileAnswers();
    var ensureChat = warmUpZac() || Promise.reject({ message: 'ZacAPI unavailable on this page' });
    return ensureChat.then(function () { return api.submitProfile(answers); }).then(function (res) {
      /* hand the recommendation to the chat drawer so it renders on open,
         and persist both (cookies) so return visits skip the questionnaire —
         each quiz run overwrites with the latest */
      if (api.state) api.state.profileReply = res;
      if (api.saveProfile) api.saveProfile(answers);
      if (api.saveReply) api.saveReply(res);
      /* On a phone "Reveal" navigates to /pages/zac-chat, which tears this JS
         context down — api.state.profileReply does not survive it. Mirror the
         full reply into sessionStorage so the chat renders the same card there
         as the desktop drawer does in-page. */
      if (api.saveHandoff) api.saveHandoff(res);
      return res;
    });
  }

  /* ── Result match table — keyed by the dominant axis (archetype) ─────── */
  var ARCH = {
    aggression: { name: 'The Aggressor', grade: 'Aspire', model: '6A', why: 'You take the attack on: a powerful, toe-weighted profile rewards your intent.' },
    power:      { name: 'The Powerhouse', grade: 'Player Edition', model: '6A', why: 'Raw power through the line: a full profile with a low middle suits you best.' },
    control:    { name: 'The Anchor', grade: 'Inspire', model: '3B', why: 'You bat time and place the ball: a balanced, control-led bat is your match.' },
    timing:     { name: 'The Timer', grade: 'Passion', model: '4A', why: 'You score on touch and timing: a light, even-balanced bat lets your hands work.' },
    pickup:     { name: 'Quick Hands', grade: 'Passion', model: '4A', why: 'Fast hands and quick pickup: a light, responsive bat keeps pace with you.' },
    balance:    { name: 'The All-Rounder', grade: 'Passion', model: '5A', why: 'You bat the situation: an even, all-round profile covers every gear.' }
  };

  /* Reference photos only exist for the 17 base models (1A–6D) — exact match
     only, no fallback to a base model for a size/Harrow variant. */
  /* ── State ───────────────────────────────────────────────────────────── */
  var state = { idx: -1, answers: {}, scores: zero() };
  function zero() { var o = {}; AXES.forEach(function (a) { o[a] = 0; }); return o; }
  function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

  /* ── Continue button: only for adjustable/typed inputs (stepper, slider,
     fields). Single-select "pick one" types (tiles/options/chips/tiers/path)
     have no Continue — choosing an option commits it immediately, same as a
     Continue click, which is also what advances state.idx and therefore the
     performance-map progress on the right (see lockedCount()). */
  var CONTINUE_TYPES = ['stepper', 'slider', 'fields'];
  function needsContinue(q) { return CONTINUE_TYPES.indexOf(q.type) !== -1; }
  var advanceTimer = null;

  var qEl = document.getElementById('bf-q');
  var topEl = document.getElementById('bf-top');
  var radar = document.getElementById('bf-radar');
  var archEl = document.getElementById('bf-arch');
  var mappedEl = document.getElementById('bf-mapped');

  /* ── Figure silhouette (used by the gender tiles on the question side) ─ */
  function figureSVG(gender) {
    if (gender === 'male') {
      return '<svg viewBox="0 0 60 150" fill="currentColor" aria-hidden="true"><circle cx="30" cy="13" r="9.5"/><rect x="17" y="26" width="26" height="46" rx="8"/><rect x="19" y="70" width="9.5" height="70" rx="4.5"/><rect x="31.5" y="70" width="9.5" height="70" rx="4.5"/></svg>';
    }
    if (gender === 'female') {
      return '<svg viewBox="0 0 60 150" fill="currentColor" aria-hidden="true"><circle cx="30" cy="13" r="9.5"/><path d="M30 26c6 0 9 3.5 10.5 8.5L47 66c1 3.2-1.2 6-4.5 6h-25c-3.3 0-5.5-2.8-4.5-6l6.5-31.5C21 29.5 24 26 30 26z"/><rect x="21" y="70" width="8.5" height="70" rx="4.25"/><rect x="30.5" y="70" width="8.5" height="70" rx="4.25"/></svg>';
    }
    /* neutral / abstract before gender is chosen */
    return '<svg viewBox="0 0 60 150" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 3" aria-hidden="true"><circle cx="30" cy="16" r="10"/><path d="M18 34 H42 L45 90 H37 L35 148 H25 L23 90 H15 Z"/></svg>';
  }

  /* ── Radar geometry ──────────────────────────────────────────────────── */
  var CX = 100, CY = 100, R = 78;
  function axisPoint(i, v) {
    var ang = (-90 + i * (360 / AXES.length)) * Math.PI / 180;
    return [CX + R * v * Math.cos(ang), CY + R * v * Math.sin(ang)];
  }

  var axisLabels = [];

  /* Radius from centre to the hexagon boundary at any angle (deg). Vertices
     sit on the axes (-90 + 60k); the edge dips to the apothem between them. */
  function hexRadius(phiDeg) {
    var beta = (((phiDeg + 90) % 60) + 60) % 60;           /* 0..60 from nearest vertex */
    return R * Math.cos(Math.PI / 6) / Math.cos((beta - 30) * Math.PI / 180);
  }

  function drawRadarScaffold() {
    var ns = 'http://www.w3.org/2000/svg';
    radar.innerHTML = '';
    axisLabels = [];
    /* faint TARGET boundary (the goal) — always shown */
    [0.34, 0.67, 1].forEach(function (r) {
      var pts = AXES.map(function (_, i) { return axisPoint(i, r).join(','); }).join(' ');
      var poly = document.createElementNS(ns, 'polygon');
      poly.setAttribute('points', pts);
      poly.setAttribute('class', r === 1 ? 'bf__radar-target' : 'bf__radar-ring');
      radar.appendChild(poly);
    });
    /* the gold progress sweep (filled segment-by-segment toward full) */
    var sweep = document.createElementNS(ns, 'path');
    sweep.setAttribute('class', 'bf__radar-sweep'); sweep.setAttribute('id', 'bf-radar-sweep');
    sweep.setAttribute('d', '');
    radar.appendChild(sweep);
    /* axes + labels (labels brighten as their slice fills) */
    AXES.forEach(function (a, i) {
      var p = axisPoint(i, 1);
      var line = document.createElementNS(ns, 'line');
      line.setAttribute('x1', CX); line.setAttribute('y1', CY);
      line.setAttribute('x2', p[0]); line.setAttribute('y2', p[1]);
      line.setAttribute('class', 'bf__radar-axis');
      radar.appendChild(line);
      var lp = axisPoint(i, 1.16);
      var t = document.createElementNS(ns, 'text');
      t.setAttribute('x', lp[0]); t.setAttribute('y', lp[1]);
      t.setAttribute('class', 'bf__radar-alabel');
      t.setAttribute('text-anchor', lp[0] < CX - 5 ? 'end' : lp[0] > CX + 5 ? 'start' : 'middle');
      t.setAttribute('dominant-baseline', 'middle');
      t.textContent = a; /* full word — viewBox is padded so long labels never clip */
      radar.appendChild(t);
      axisLabels.push(t);
    });
  }

  /* Locked questions → an even fraction of the whole map. Only questions the
     player has COMMITTED with Continue count (state.idx) — selecting an option
     does not advance the fill; pressing Continue does. Ends exactly full. */
  function lockedCount() {
    var total = activeQuestions().length;
    if (state.idx < 0) return 0;                       /* intro */
    if (state.idx >= total) return total;              /* result */
    return state.idx;
  }

  /* Wedge path tracing the hexagon boundary from top, clockwise, for `frac`. */
  function sweepPath(frac) {
    if (frac <= 0) return '';
    if (frac >= 1) frac = 1;
    var deg = frac * 360, step = 2, pts = [CX.toFixed(2) + ',' + CY.toFixed(2)], t = 0;
    while (t < deg) {
      var phi = -90 + t, r = hexRadius(phi);
      pts.push((CX + r * Math.cos(phi * Math.PI / 180)).toFixed(2) + ',' + (CY + r * Math.sin(phi * Math.PI / 180)).toFixed(2));
      t += step;
    }
    var pe = -90 + deg, re = hexRadius(pe);
    pts.push((CX + re * Math.cos(pe * Math.PI / 180)).toFixed(2) + ',' + (CY + re * Math.sin(pe * Math.PI / 180)).toFixed(2));
    return 'M' + pts.join(' L') + ' Z';
  }

  function updateRadar() {
    var sweep = document.getElementById('bf-radar-sweep');
    if (!sweep) return;
    var locked = lockedCount();
    var frac = clamp(locked / activeQuestions().length, 0, 1);
    var deg = frac * 360;
    sweep.setAttribute('d', sweepPath(frac));
    /* brighten each axis label once the sweep has passed its spoke */
    axisLabels.forEach(function (t, i) {
      t.classList.toggle('bf__radar-alabel--on', deg >= i * (360 / AXES.length) - 1 && frac > 0);
    });
    /* % mapped counter */
    if (mappedEl) mappedEl.textContent = Math.round(frac * 100) + '% mapped';
  }

  /* ── Archetype (dominant axis) ───────────────────────────────────────── */
  function dominantAxis() {
    var best = 'balance', bv = -1;
    AXES.forEach(function (a) { if (state.scores[a] > bv) { bv = state.scores[a]; best = a; } });
    return bv <= 0 ? null : best;
  }
  function updateArch() {
    if (!archEl) return;
    var answered = Object.keys(state.answers).length;
    var dom = dominantAxis();
    if (!dom || answered < 3) { archEl.innerHTML = 'archetype · <b>calibrating…</b>'; return; }
    var firm = answered >= 9 ? '' : '(emerging) ';
    archEl.innerHTML = 'archetype · <b>' + firm + ARCH[dom].name + '</b>';
  }

  /* ── Running log (removed — the progressive map replaces the answer log) ─ */

  /* ── Figure + height marker ──────────────────────────────────────────── */
  /* (removed — the player silhouette + height scale no longer sit on the map) */

  /* ── Apply / remove an answer's score deltas ─────────────────────────── */
  function recomputeScores() {
    state.scores = zero();
    /* All questions, not just the asked ones: the three auto-filled 'Both'
       answers are real selections now, so they score like any other. 'Both'
       is balance-weighted on all three, so a junior's map reads balanced
       rather than skewed — and the radar agrees with the profile we send. */
    QUESTIONS.forEach(function (q) {
      var a = state.answers[q.id];
      if (!a) return;
      var s = a.score || (q.score && q.score(a.value)) || null;
      if (s) AXES.forEach(function (ax) { if (s[ax]) state.scores[ax] = clamp(state.scores[ax] + s[ax], 0, 100); });
    });
  }

  function setAnswer(q, value, label, scoreObj) {
    state.answers[q.id] = { value: value, label: label, score: scoreObj || null };
    /* Height may have just changed which side of 5' the player is on, which
       decides whether the three shot-preference answers are auto-filled. */
    syncKidAnswers();
    recomputeScores();
    updateMap();
  }

  function updateMap() {
    updateRadar();
  }

  /* ════════════════════════════════════════════════════════════════════
     RENDER — intro (idx -1), questions, result. state.idx indexes the LIVE
     list, so an under-5' run is 10 questions instead of 13 and its result
     step lands three earlier.
     ════════════════════════════════════════════════════════════════════ */
  function render() {
    var qs = activeQuestions();
    if (state.idx < 0) return renderIntro();
    if (state.idx >= qs.length) return renderResult();
    renderQuestion(qs[state.idx]);
  }

  /* section-batfit.js executes BEFORE zac-api.js (deferred scripts run in
     document order; the zac-chat section sits at the end of <body>), so the
     saved match must be read straight from the cookie — window.ZacAPI is
     not available yet when the intro first renders. */
  function savedReply() {
    if (window.ZacAPI && window.ZacAPI.getReply) return window.ZacAPI.getReply();
    try {
      var m = document.cookie.match(/(?:^|; )zc_reply=([^;]*)/);
      return m ? JSON.parse(decodeURIComponent(m[1])) : null;
    } catch (e) { return null; }
  }

  /* Same story for the saved profile answers (zc_profile) — read the cookie
     directly rather than waiting on window.ZacAPI. Used to pre-fill the
     name / email / phone step so a returning player never retypes them. */
  /* "Retake" deliberately wipes zc_profile so the new run yields a fresh
     recommendation — but the contact details should survive that wipe, since
     retaking IS the second-fill case we want pre-filled. Snapshot them here
     before clearing and fall back to the snapshot for the rest of the run. */
  var retainedContact = null;

  function savedProfile() {
    var a = null;
    if (window.ZacAPI && window.ZacAPI.getProfile) {
      var p = window.ZacAPI.getProfile();
      a = (p && p.answers) || null;
    } else {
      try {
        var m = document.cookie.match(/(?:^|; )zc_profile=([^;]*)/);
        var o = m ? JSON.parse(decodeURIComponent(m[1])) : null;
        a = (o && o.answers) || null;
      } catch (e) { a = null; }
    }
    return a || retainedContact;
  }

  function clearFitCookies() {
    if (window.ZacAPI && window.ZacAPI.clearFit) return window.ZacAPI.clearFit();
    try {
      ['zc_profile', 'zc_reply'].forEach(function (n) {
        document.cookie = n + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
      });
    } catch (e) {}
  }

  function renderIntro() {
    var saved = savedReply();
    if (saved) return renderWelcomeBack(saved);
    qEl.innerHTML =
      '<div class="bf__intro">' +
        '<p class="bf__intro-eyebrow"><span class="dot"></span>AI Bat Fit · Zac.ai</p>' +
        '<h1 class="bf__intro-title">Find the bat built for your game.</h1>' +
        '<p class="bf__intro-sub">12 questions · ~60 seconds · your performance map, drawn live.</p>' +
        '<button type="button" class="bf__intro-btn" id="bf-start">Start the AI Bat Fit ' + arrow() + '</button>' +
      '</div>';
    document.getElementById('bf-start').addEventListener('click', function () {
      /* Meta: quiz entered. Paired with Lead this gives completion rate --
         a 12-question run is long, and without both ends you cannot see
         where players drop. Once per page load. */
      if (!window.__zbatQuizStarted) {
        window.__zbatQuizStarted = true;
        zbatFb(function (fbq) { fbq('trackCustom', 'BatFitStarted'); });
      }
      state.idx = 0; render();
    });
    if (topEl) topEl.innerHTML = '';
    updateMap();
  }

  /* Returning player: a match is already saved in the cookie — offer it
     before making them redo all 12 questions. Retake wipes the saved
     profile + match so the new run produces a fresh recommendation. */
  function renderWelcomeBack(saved) {
    var c = saved.catalog_details || {};
    var name = String(c.name || saved.bat_sr_no || 'your bat').replace(/[<>&]/g, '');
    var model = (window.ZacAPI && window.ZacAPI.modelForZcode) ? window.ZacAPI.modelForZcode(saved.bat_sr_no) : null;
    qEl.innerHTML =
      '<div class="bf__intro">' +
        '<p class="bf__intro-eyebrow"><span class="dot"></span>AI Bat Fit · Zac.ai</p>' +
        '<h1 class="bf__intro-title">Welcome back — your match is ready.</h1>' +
        '<p class="bf__intro-sub">Last time you matched with ' + name + (model ? ' · Model ' + model : '') + '.</p>' +
        '<div class="bf__intro-actions">' +
          '<button type="button" class="bf__intro-btn" id="bf-view">View my match ' + arrow() + '</button>' +
          '<button type="button" class="bf__intro-btn bf__intro-btn--ghost" id="bf-retake">Retake AI Bat Fit Test</button>' +
        '</div>' +
      '</div>';
    document.getElementById('bf-view').addEventListener('click', function () {
      if (window.ZacChat && window.ZacChat.open) window.ZacChat.open();
    });
    document.getElementById('bf-retake').addEventListener('click', function () {
      var prev = savedProfile();
      if (prev) retainedContact = { name: prev.name || '', email: prev.email || '', phone: prev.phone || '' };
      clearFitCookies();
      state.idx = 0; render();
    });
    if (topEl) topEl.innerHTML = '';
    updateMap();
  }

  function progressHead(q) {
    var n = state.idx + 1;
    var total = activeQuestions().length, scored = scoredCount();
    var pct = Math.round((n / total) * 100);
    var displayN = Math.min(n, scored);
    return '<div class="bf__prow"><span class="bf__prog">Question ' + (displayN < 10 ? '0' + displayN : displayN) + ' / ' + scored + '</span>' +
      '<span class="bf__state">' + (state.idx >= total - 1 ? 'finalising' : 'calibrating') + '</span></div>' +
      '<div class="bf__pbar"><span class="bf__pfill" style="width:' + pct + '%"></span></div>';
  }

  function renderQuestion(q) {
    var body = '';
    if (q.type === 'tiles') body = renderTiles(q);
    else if (q.type === 'options') body = renderOptions(q);
    else if (q.type === 'chips') body = renderChips(q);
    else if (q.type === 'path') body = renderPath(q);
    else if (q.type === 'tiers') body = renderTiers(q);
    else if (q.type === 'stepper') body = renderStepper(q);
    else if (q.type === 'slider') body = renderSlider(q);
    else if (q.type === 'fields') body = renderFields(q);

    /* scoring gets the two-handedness wagon-wheel display above its options */
    if (q.id === 'scoring') {
      var selNow = state.answers.scoring && state.answers.scoring.value;
      body = '<div class="bf__wheels" id="bf-wheels">' + wheelsInner(selNow || null) + '</div>' + body;
    }

    if (topEl) topEl.innerHTML = progressHead(q);
    var showBack = state.idx > 0;
    var showContinue = needsContinue(q);
    var navHtml = '';
    if (showBack || showContinue) {
      navHtml = '<div class="bf__nav">' +
        (showBack ? '<button type="button" class="bf__back" id="bf-back">← Back</button>' : '') +
        (showContinue ? '<button type="button" class="bf__continue" id="bf-continue">' + (state.idx >= activeQuestions().length - 1 ? 'See my match' : 'Continue') + ' ' + arrow() + '</button>' : '') +
      '</div>';
    }
    qEl.innerHTML =
      '<div class="bf__q-inner">' +
        '<h2 class="bf__q-title">' + q.q + '</h2>' +
        '<p class="bf__why">' + q.why + '</p>' +
        body +
        navHtml +
      '</div>';

    wireQuestion(q);
    updateMap();
    syncContinue(q);
  }

  function arrow() { return '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>'; }

  /* renderers return HTML; wireQuestion binds events + reflects stored answer */
  function renderTiles(q) {
    var cur = state.answers[q.id] && state.answers[q.id].value;
    return '<div class="bf__tiles" style="--cols:' + (q.cols || 2) + '" role="radiogroup" aria-label="' + q.q + '">' +
      q.options.map(function (o) {
        var fig;
        if (q.id === 'gender') fig = figureSVG(o.v);
        else if (q.id === 'bat') {
          /* GPT-generated product images (assets/bf-bat-*.png) take over when present;
             the inline icon stays as the fallback until then */
          var img = window.BF_ASSETS && (o.v === 'leather' ? window.BF_ASSETS.batLeather : window.BF_ASSETS.batTennis);
          fig = img
            ? '<img class="bf__tile-img" src="' + img + '" alt="" onerror="this.hidden=true;this.nextElementSibling.hidden=false"><span hidden>' + batBallIcon(o.v) + '</span>'
            : batBallIcon(o.v);
        } else fig = batIcon();
        return '<button type="button" class="bf__tile' + (o.soon ? ' bf__tile--soon' : '') + '" role="radio" data-v="' + o.v + '"' +
          (o.soon ? ' disabled aria-disabled="true"' : '') + ' aria-checked="' + (!o.soon && cur === o.v) + '">' +
          '<span class="bf__tile-fig">' + fig + '</span><span class="bf__tile-label">' + o.label + '</span>' +
          (o.soon ? '<span class="bf__tile-soon">Coming soon</span>' : '') + '</button>';
      }).join('') + '</div>';
  }
  function batIcon() { return '<svg viewBox="0 0 24 60" width="20" fill="currentColor" aria-hidden="true"><rect x="10" y="2" width="4" height="16" rx="2"/><rect x="7" y="18" width="10" height="40" rx="4"/></svg>'; }

  /* ── Question iconography — one stroke language, currentColor, gold on select ── */
  function batBallIcon(kind) {
    var bat = '<rect x="20" y="2" width="4" height="14" rx="2" fill="currentColor"/><rect x="17" y="16" width="10" height="38" rx="4" fill="currentColor"/>';
    var ball;
    if (kind === 'leather') {
      ball = '<circle cx="34" cy="47" r="7" fill="var(--white, #fff)" stroke="currentColor" stroke-width="1.5"/>' +
        '<line x1="34" y1="40.5" x2="34" y2="53.5" stroke="currentColor" stroke-width="1.1"/>' +
        '<path d="M31.7 43.5h1.5M31.7 47h1.5M31.7 50.5h1.5M34.8 43.5h1.5M34.8 47h1.5M34.8 50.5h1.5" stroke="currentColor" stroke-width="0.9"/>';
    } else {
      ball = '<circle cx="34" cy="47" r="7" fill="var(--white, #fff)" stroke="currentColor" stroke-width="1.5"/>' +
        '<path d="M28.7 43.2q5 3.8 0 7.6M39.3 43.2q-5 3.8 0 7.6" fill="none" stroke="currentColor" stroke-width="1.1"/>';
    }
    return '<svg viewBox="0 0 44 60" width="32" aria-hidden="true">' + bat + ball + '</svg>';
  }

  /* batting-order strip: 11 slots, the option's slots lit */
  function orderStrip(lit) {
    var dots = '';
    for (var i = 0; i < 11; i++) {
      var on = lit.indexOf(i) !== -1;
      dots += '<circle cx="' + (5 + i * 7) + '" cy="5" r="2.7" ' +
        (on ? 'fill="currentColor"' : 'fill="none" stroke="currentColor" stroke-opacity="0.4" stroke-width="1"') + '/>';
    }
    return '<svg viewBox="0 0 80 10" width="78" aria-hidden="true">' + dots + '</svg>';
  }

  /* footwork marks: two foot pills + direction of movement */
  function footIcon(kind) {
    var lead = '<rect x="7" y="5" width="6.5" height="16" rx="3.25" ';
    var trail = '<rect x="16.5" y="5" width="6.5" height="16" rx="3.25" ';
    var fill = 'fill="currentColor"/>', line = 'fill="none" stroke="currentColor" stroke-opacity="0.5" stroke-width="1.2"/>';
    var arrow = '';
    if (kind === 'front') { arrow = '<path d="M29 18V8M26 11l3-3 3 3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'; return '<svg viewBox="0 0 34 26" width="30" aria-hidden="true">' + lead + fill + trail + line + arrow + '</svg>'; }
    if (kind === 'back') { arrow = '<path d="M29 8v10M26 15l3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'; return '<svg viewBox="0 0 34 26" width="30" aria-hidden="true">' + lead + line + trail + fill + arrow + '</svg>'; }
    return '<svg viewBox="0 0 34 26" width="30" aria-hidden="true">' + lead + fill + trail + fill + '</svg>';
  }

  /* Scoring viz: two wagon wheels (right-hand & left-hand bat), drawn as the
     ground looks when you FACE the batsman. A right-hander's leg side is his
     left, which appears on the viewer's RIGHT; his off side appears on the
     viewer's LEFT — mirrored for a left-hander.

     That convention is only obvious if you can see WHERE the batsman stands,
     so the batsman is drawn explicitly at the top of the wheel, facing the
     viewer down the pitch. Without the figure the
     circle reads equally well as "batsman at the near end", under which the
     OFF/LEG labels look reversed — the labels are correct, the viewpoint was
     just left implicit. The figure is the thing that makes it unambiguous, so
     it is not decoration: do not drop it. */
  function handWheel(hand, sel) {
    var offRight = hand === 'left'; /* viewer-side on which OFF appears */
    var right = '<path d="M22 6 A16 16 0 0 1 22 38 Z" fill="var(--gold-on-white, #a8861d)"/>';
    var left = '<path d="M22 6 A16 16 0 0 0 22 38 Z" fill="var(--gold-on-white, #a8861d)"/>';
    var shade = '';
    if (sel === 'both') shade = right + left;
    else if (sel === 'off') shade = offRight ? right : left;
    else if (sel === 'on') shade = offRight ? left : right;
    var labs = offRight
      ? '<text x="11" y="24" text-anchor="middle">LEG</text><text x="33" y="24" text-anchor="middle">OFF</text>'
      : '<text x="11" y="24" text-anchor="middle">OFF</text><text x="33" y="24" text-anchor="middle">LEG</text>';

    /* Batsman above the top of the circle, facing down the pitch toward the
       viewer. The bat is held on the side his hands are: a right-hander's bat
       sits to the viewer's LEFT of him (his off side), mirrored for a
       left-hander — so the figure itself corroborates the labels rather than
       sitting neutral between them.

       He lives ABOVE y=6 (the circle's top). The wheel's gold shading fills
       the circle, so anything drawn inside it gets swallowed — the figure has
       to clear the arc to stay legible. */
    var batX = offRight ? 27.4 : 16.6;
    var batsman =
      '<circle cx="22" cy="-7.4" r="3.1" fill="currentColor"/>' +
      '<path d="M18.6 -3.4 h6.8 l-1.2 8 h-4.4 z" fill="currentColor"/>' +
      '<rect x="' + (batX - 0.85) + '" y="-3.6" width="1.7" height="3.6" rx="0.85" fill="currentColor"/>' +
      '<rect x="' + (batX - 1.8) + '" y="-0.4" width="3.6" height="5" rx="1.4" fill="currentColor"/>';

    /* No stumps: at the sizes this renders (68–110px) they sat behind the
       torso and read as a smudge rather than as a wicket. The figure alone
       fixes the orientation, which is the whole job here. */

    return '<svg viewBox="0 -12 44 56" width="110" aria-hidden="true" class="bf__wheel-svg">' +
      '<g opacity="' + (shade ? '0.55' : '0') + '">' + shade + '</g>' +
      '<circle cx="22" cy="22" r="16" fill="none" stroke="currentColor" stroke-width="1.2"/>' +
      '<line x1="22" y1="6" x2="22" y2="38" stroke="currentColor" stroke-width="0.9"/>' +
      batsman +
      '<g font-size="5.4" fill="currentColor" font-family="inherit" letter-spacing="0.5">' + labs + '</g></svg>';
  }
  function wheelsInner(sel) {
    return '<figure class="bf__wheel">' + handWheel('right', sel) + '<figcaption class="bf__wheel-cap">Right-hand bat</figcaption></figure>' +
      '<figure class="bf__wheel">' + handWheel('left', sel) + '<figcaption class="bf__wheel-cap">Left-hand bat</figcaption></figure>';
  }

  function natureIcon(kind) {
    var shield = 'M12 3l7 2.6v5.2c0 4.6-2.9 7.6-7 9.2-4.1-1.6-7-4.6-7-9.2V5.6z';
    var bolt = 'M13.2 3 6.5 13.5h4.2L9 21l8.5-11.5h-4.4L14.8 3z';
    if (kind === 'defensive') return '<svg viewBox="0 0 24 24" width="24" aria-hidden="true"><path d="' + shield + '" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';
    if (kind === 'slog') return '<svg viewBox="0 0 24 24" width="24" aria-hidden="true"><path d="' + bolt + '" fill="currentColor"/></svg>';
    return '<svg viewBox="0 0 24 24" width="24" aria-hidden="true"><path d="' + shield + '" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><g transform="translate(5.4 5.2) scale(0.55)"><path d="' + bolt + '" fill="currentColor"/></g></svg>';
  }

  /* bounce trajectory off a pitch line */
  function pitchIcon(kind) {
    var base = '<line x1="2" y1="19" x2="34" y2="19" stroke="currentColor" stroke-opacity="0.4" stroke-width="1.2"/>';
    var traj = kind === 'high'
      ? '<path d="M2 15 L14 19 L28 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"/><circle cx="29.5" cy="3" r="1.9" fill="currentColor"/>'
      : '<path d="M2 14 L14 19 L31 11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"/><circle cx="32.5" cy="10.3" r="1.9" fill="currentColor"/>';
    return '<svg viewBox="0 0 36 22" width="32" aria-hidden="true">' + base + traj + '</svg>';
  }

  /* body-type figure: same head, widening frame */
  function buildFig(w) {
    return '<svg viewBox="0 0 34 28" width="30" aria-hidden="true"><circle cx="17" cy="5" r="3.6" fill="currentColor"/>' +
      '<path d="M' + (17 - w / 2) + ' 11 h' + w + ' l-2 15 h-' + (w - 4) + ' z" fill="currentColor"/></svg>';
  }

  var OPT_ICONS = {
    'position:p12': orderStrip([0, 1]),
    'position:p34': orderStrip([2, 3]),
    'position:p567': orderStrip([4, 5, 6]),
    'position:p8': orderStrip([7, 8, 9, 10]),
    'foot:front': footIcon('front'),
    'foot:back': footIcon('back'),
    'foot:both': footIcon('both'),
    'nature:defensive': natureIcon('defensive'),
    'nature:slog': natureIcon('slog'),
    'nature:both': natureIcon('both'),
    'pitch:high': pitchIcon('high'),
    'pitch:standard': pitchIcon('standard')
  };

  function renderOptions(q) {
    var cur = state.answers[q.id] && state.answers[q.id].value;
    return '<div class="bf__fields" role="radiogroup" aria-label="' + q.q + '">' +
      q.options.map(function (o) {
        var ic = OPT_ICONS[q.id + ':' + o.v];
        return '<button type="button" class="bf__opt" role="radio" data-v="' + o.v + '" aria-checked="' + (cur === o.v) + '">' +
          (ic ? '<span class="bf__opt-ic" aria-hidden="true">' + ic + '</span>' : '<span class="bf__radio" aria-hidden="true"></span>') +
          '<span class="bf__opt-main"><span class="bf__opt-label">' + o.label + '</span>' +
          (o.sub ? '<span class="bf__opt-sub">' + o.sub + '</span>' : '') + '</span></button>';
      }).join('') + '</div>';
  }

  function renderChips(q) {
    var cur = state.answers[q.id] && state.answers[q.id].value;
    return '<div class="bf__chips" role="radiogroup" aria-label="' + q.q + '">' +
      q.options.map(function (o) {
        return '<button type="button" class="bf__chip" role="radio" data-v="' + o.v + '" aria-checked="' + (cur === o.v) + '">' + o.label + '</button>';
      }).join('') + '</div>';
  }

  /* progression path (experience): four nodes on a line, filled up to the pick */
  function renderPath(q) {
    var cur = state.answers[q.id] && state.answers[q.id].value;
    var idx = -1;
    q.options.forEach(function (o, i) { if (o.v === cur) idx = i; });
    var fill = idx < 0 ? 0 : idx / (q.options.length - 1);
    return '<div class="bf__path" role="radiogroup" aria-label="' + q.q + '" style="--bf-fill:' + fill + '">' +
      q.options.map(function (o, i) {
        return '<button type="button" class="bf__node" role="radio" data-v="' + o.v + '" data-i="' + i + '" aria-checked="' + (cur === o.v) + '">' +
          '<span class="bf__node-dot" aria-hidden="true"></span>' +
          '<span class="bf__node-lab">' + o.label + '</span></button>';
      }).join('') + '</div>';
  }

  function renderTiers(q) {
    var cur = state.answers[q.id] && state.answers[q.id].value;
    return '<div class="bf__tiers" role="radiogroup" aria-label="' + q.q + '">' +
      q.options.map(function (o) {
        return '<button type="button" class="bf__tier" role="radio" data-v="' + o.v + '" aria-checked="' + (cur === o.v) + '">' +
          (o.w ? '<span class="bf__tier-fig" aria-hidden="true">' + buildFig(o.w) + '</span>' : '') +
          '<span class="bf__tier-bar" style="height:' + (o.h * 0.9 + 16) + 'px"></span>' +
          '<span class="bf__tier-lab">' + o.label + '</span></button>';
      }).join('') + '</div>';
  }

  function renderStepper(q) {
    var v = (state.answers[q.id] && state.answers[q.id].value) || q.def;
    return '<div class="bf__stepper">' +
      '<button type="button" class="bf__step-btn" data-step="-1" aria-label="Decrease">−</button>' +
      '<span class="bf__readout" id="bf-readout">' + v + '<span>' + q.unit + '</span></span>' +
      '<button type="button" class="bf__step-btn" data-step="1" aria-label="Increase">+</button></div>';
  }

  function sliderDisp(q, v) { return q.fmt ? q.fmt(v) : String(v); }
  /* Most sliders carry a fixed unit ('kg', 'years'); height's varies with the
     value, since it is a live conversion rather than a label. */
  function sliderUnit(q, v) { return q.unitFor ? q.unitFor(v) : q.unit; }
  function sliderLabel(q, v) { return (sliderDisp(q, v) + ' ' + sliderUnit(q, v)).replace(/\s+/g, ' ').trim(); }

  function renderSlider(q) {
    var v = (state.answers[q.id] && state.answers[q.id].value) || q.def;
    return '<div class="bf__slider">' +
      '<span class="bf__readout" id="bf-readout">' + sliderDisp(q, v) + '<span>' + sliderUnit(q, v) + '</span></span>' +
      /* minus on the left, plus on the right: the slider is fine for a rough
         sweep, but the last kg/year is easier to land on with a tap. */
      '<div class="bf__sliderow">' +
        '<button type="button" class="bf__nudge" data-nudge="-1" aria-label="Decrease">−</button>' +
        '<div class="bf__range-col">' +
          '<input class="bf__range" type="range" id="bf-range" min="' + q.min + '" max="' + q.max + '" step="' + q.step + '" value="' + v + '" aria-label="' + q.q + '">' +
          '<div class="bf__ticks"><span>' + sliderDisp(q, q.min) + '</span><span>' + sliderDisp(q, Math.round((q.min + q.max) / 2)) + '</span><span>' + sliderDisp(q, q.max) + '</span></div>' +
        '</div>' +
        '<button type="button" class="bf__nudge" data-nudge="1" aria-label="Increase">+</button>' +
      '</div></div>';
  }

  /* Answers typed earlier in THIS run win; otherwise fall back to the details
     saved by the last completed quiz (zc_profile cookie, 30 days) so a second
     run arrives pre-filled. "Start fresh" clears the cookie, so it also
     clears the pre-fill. */
  /* ── WhatsApp number: country code ───────────────────────────────────────
     The number collected here is the ONLY way a shopper can ever be reached on
     WhatsApp — it is what Fire AI store against the chat_id and message from.
     WhatsApp addresses are E.164, so a bare "9876543210" or a UK "07700 900123"
     is undeliverable. We therefore never store what was typed: we store
     '+' + dial + national, and the picker exists so the dial part is a choice
     rather than a guess.

     Ordered by market, not alphabetically — India is the overwhelming majority
     of traffic and belongs at the top; UK and US follow because the store sells
     into both (see snippets/money.liquid). */
  var DIALS = [
    { c: 'IN', d: '91' },  { c: 'GB', d: '44' },  { c: 'US', d: '1' },
    { c: 'AE', d: '971' }, { c: 'AU', d: '61' },  { c: 'NZ', d: '64' },
    { c: 'ZA', d: '27' },  { c: 'CA', d: '1' },   { c: 'SG', d: '65' },
    { c: 'MY', d: '60' },  { c: 'LK', d: '94' },  { c: 'PK', d: '92' },
    { c: 'BD', d: '880' }, { c: 'NP', d: '977' }, { c: 'AF', d: '93' },
    { c: 'SA', d: '966' }, { c: 'QA', d: '974' }, { c: 'OM', d: '968' },
    { c: 'KW', d: '965' }, { c: 'BH', d: '973' }, { c: 'IE', d: '353' },
    { c: 'KE', d: '254' }, { c: 'ZW', d: '263' }, { c: 'JM', d: '1876' },
    { c: 'TT', d: '1868' },{ c: 'HK', d: '852' }, { c: 'DE', d: '49' },
    { c: 'FR', d: '33' },  { c: 'NL', d: '31' }
  ];

  /* Shopify tells us the market the shopper is browsing in; use it so the
     common case needs no interaction at all. Falls back to India. */
  function defaultDial() {
    try {
      var c = window.Shopify && window.Shopify.country;
      for (var i = 0; i < DIALS.length; i++) if (DIALS[i].c === c) return DIALS[i].d;
    } catch (e) {}
    return '91';
  }

  /* Longest match wins, so +1876 (Jamaica) is not mistaken for +1. */
  function dialFor(stored) {
    var s = String(stored || '');
    if (s.charAt(0) !== '+') return defaultDial();
    var best = '';
    for (var i = 0; i < DIALS.length; i++) {
      var d = DIALS[i].d;
      if (s.indexOf('+' + d) === 0 && d.length > best.length) best = d;
    }
    return best || defaultDial();
  }

  function stripDial(stored, dial) {
    var s = String(stored || '');
    return (s.indexOf('+' + dial) === 0) ? s.slice(dial.length + 1) : s.replace(/^\+/, '');
  }

  /* A shopper who types the full international number themselves is trusted
     over the picker. Otherwise the leading trunk zero — 07700…, 09876… — is
     dropped, because it does not belong in an international number. */
  function normalisePhone(dial, raw) {
    var s = String(raw || '').trim();
    if (s.charAt(0) === '+') return '+' + s.slice(1).replace(/\D/g, '');
    s = s.replace(/\D/g, '').replace(/^0+/, '');
    return s ? '+' + dial + s : '';
  }

  /* Same one phone rule as the backend (Code.gs normPhone_): a 10-digit Indian
     mobile -> E.164, or a foreign number that already carries its own +. Used
     when the shopper typed a leading + themselves, overriding the picker. */
  function oneRulePhone(raw) {
    var s = String(raw || '').trim();
    var d = s.replace(/\D/g, '');
    if (!d) return '';
    var nat;
    if (d.slice(0, 2) === '91') {
      nat = d.slice(2);
      return /^[6-9]\d{9}$/.test(nat) ? '+91' + nat : '';
    }
    return (d.length >= 8 && d.length <= 15) ? '+' + d : '';
  }

  /* The dial-code picker narrows the check: an Indian dial code must resolve
     to a real 10-digit mobile, not just any 8–15 digit string. Every other
     dial keeps the old loose check, since we don't carry per-country rules. */
  function phoneValid(dial, raw) {
    var s = String(raw || '').trim();
    if (s.charAt(0) === '+') return !!oneRulePhone(s);
    if (dial === '91') {
      var nat = s.replace(/\D/g, '').replace(/^0+/, '');
      return /^[6-9]\d{9}$/.test(nat);
    }
    return /^\+\d{8,15}$/.test(normalisePhone(dial, raw));
  }

  function renderFields(q) {
    var a = state.answers[q.id] && state.answers[q.id].value || {};
    var prev = savedProfile() || {};
    return '<div class="bf__fields">' +
      q.fields.map(function (f) {
        var v = a[f.k] || prev[f.k] || '';
        /* autocomplete tokens are a fixed vocabulary: the phone field's is
           "tel", not "phone", or the browser will not offer to fill it. */
        var ac = (f.k === 'phone') ? 'tel' : f.k;

        if (!f.dial) {
          return '<label class="bf__field">' +
            '<span class="bf__label">' + f.placeholder + '</span>' +
            '<input class="bf__input" type="' + f.type + '" data-k="' + f.k + '" placeholder="' + f.placeholder + '" value="' + attr(v) + '" autocomplete="' + ac + '">' +
            '</label>';
        }

        var picked = dialFor(v);
        var national = stripDial(v, picked);
        return '<label class="bf__field">' +
          '<span class="bf__label">' + f.placeholder + '</span>' +
          '<span class="bf__tel">' +
            '<select class="bf__dial" data-dial="1" aria-label="Country dialling code">' +
              DIALS.map(function (d) {
                return '<option value="' + d.d + '"' + (d.d === picked ? ' selected' : '') + '>' + d.c + ' +' + d.d + '</option>';
              }).join('') +
            '</select>' +
            '<input class="bf__input" type="tel" data-k="' + f.k + '" placeholder="' + f.placeholder + '" value="' + attr(national) + '" autocomplete="' + ac + '">' +
          '</span>' +
          '</label>';
      }).join('') + '</div>';
  }

  function attr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ── Wire a rendered question's controls ─────────────────────────────── */
  function wireQuestion(q) {
    var cont = document.getElementById('bf-continue');
    var back = document.getElementById('bf-back');
    if (back) back.addEventListener('click', function () { window.clearTimeout(advanceTimer); state.idx--; render(); });
    if (cont) cont.addEventListener('click', function () {
      if (cont.disabled) return;
      commitField(q);
      state.idx++; render();
    });

    if (['tiles', 'options', 'chips', 'tiers', 'path'].indexOf(q.type) !== -1) {
      var clsByType = { tiles: 'bf__tile', options: 'bf__opt', chips: 'bf__chip', tiers: 'bf__tier', path: 'bf__node' };
      var sel = '.' + clsByType[q.type];
      Array.prototype.forEach.call(qEl.querySelectorAll(sel), function (btn) {
        btn.addEventListener('click', function () {
          var o = findOpt(q, btn.getAttribute('data-v'));
          Array.prototype.forEach.call(qEl.querySelectorAll(sel), function (b) { b.setAttribute('aria-checked', String(b === btn)); });
          setAnswer(q, o.v, o.label, o.s || null);
          if (q.id === 'scoring') {
            var wheels = document.getElementById('bf-wheels');
            if (wheels) wheels.innerHTML = wheelsInner(o.v);
          }
          if (q.type === 'path') {
            var track = qEl.querySelector('.bf__path');
            if (track) track.style.setProperty('--bf-fill', String(parseInt(btn.getAttribute('data-i'), 10) / (q.options.length - 1)));
          }
          flyTag(btn, o.s);
          /* no Continue on these question types — picking an option commits it
             immediately (debounced so a fast re-pick before the transition
             fires re-targets it instead of advancing twice) */
          window.clearTimeout(advanceTimer);
          advanceTimer = window.setTimeout(function () { state.idx++; render(); }, reduce ? 0 : 320);
        });
      });
    } else if (q.type === 'stepper') {
      var v0 = (state.answers[q.id] && state.answers[q.id].value) || q.def;
      if (!state.answers[q.id]) setAnswer(q, v0, v0 + ' ' + q.unit);
      Array.prototype.forEach.call(qEl.querySelectorAll('[data-step]'), function (b) {
        b.addEventListener('click', function () {
          var cur = state.answers[q.id].value + parseInt(b.getAttribute('data-step'), 10);
          cur = clamp(cur, q.min, q.max);
          document.getElementById('bf-readout').innerHTML = cur + '<span>' + q.unit + '</span>';
          setAnswer(q, cur, cur + ' ' + q.unit);
        });
      });
    } else if (q.type === 'slider') {
      var rng = document.getElementById('bf-range');
      var v1 = (state.answers[q.id] && state.answers[q.id].value) || q.def;
      if (!state.answers[q.id]) setAnswer(q, v1, sliderLabel(q, v1));
      paintRange(rng);
      var nudges = qEl.querySelectorAll('[data-nudge]');
      /* grey out whichever nudge has nowhere left to go */
      function syncNudges() {
        var cur = parseInt(rng.value, 10);
        Array.prototype.forEach.call(nudges, function (b) {
          var dir = parseInt(b.getAttribute('data-nudge'), 10);
          b.disabled = dir < 0 ? cur <= q.min : cur >= q.max;
        });
      }
      rng.addEventListener('input', function () {
        var cur = parseInt(rng.value, 10);
        document.getElementById('bf-readout').innerHTML = sliderDisp(q, cur) + '<span>' + sliderUnit(q, cur) + '</span>';
        paintRange(rng);
        syncNudges();
        setAnswer(q, cur, sliderLabel(q, cur));
      });
      Array.prototype.forEach.call(nudges, function (b) {
        b.addEventListener('click', function () {
          var step = (q.step || 1) * parseInt(b.getAttribute('data-nudge'), 10);
          rng.value = clamp(parseInt(rng.value, 10) + step, q.min, q.max);
          rng.dispatchEvent(new Event('input'));
        });
      });
      syncNudges();
    } else if (q.type === 'fields') {
      warmUpZac(); /* user is typing details — fetch the chat_id in the background */
      Array.prototype.forEach.call(qEl.querySelectorAll('.bf__input'), function (inp) {
        inp.addEventListener('input', function () { syncContinue(q); });
      });
      var dialSel = qEl.querySelector('.bf__dial');
      if (dialSel) dialSel.addEventListener('change', function () { syncContinue(q); });
    }
  }

  /* a tiny "+ AXIS" tag rises from the chosen option toward the map */
  function flyTag(btn, s) {
    if (reduce || !s) return;
    var top = null, mx = -Infinity;
    for (var k in s) { if (s[k] > mx) { mx = s[k]; top = k; } }
    if (!top) return;
    var r = btn.getBoundingClientRect();
    var tag = document.createElement('span');
    tag.className = 'bf__fly';
    tag.textContent = '+ ' + top.toUpperCase();
    tag.style.left = (r.right - 14) + 'px';
    tag.style.top = (r.top + r.height / 2 - 8) + 'px';
    document.body.appendChild(tag);
    requestAnimationFrame(function () { tag.classList.add('bf__fly--go'); });
    setTimeout(function () { tag.remove(); }, 750);
  }

  function paintRange(rng) {
    var pct = (rng.value - rng.min) / (rng.max - rng.min) * 100;
    rng.style.background = 'linear-gradient(90deg, var(--gold-on-white) 0%, var(--gold-on-white) ' + pct + '%, var(--light-hairline) ' + pct + '%, var(--light-hairline) 100%)';
  }

  function findOpt(q, v) { var f = null; q.options.forEach(function (o) { if (o.v === v) f = o; }); return f; }

  function commitField(q) {
    if (q.type !== 'fields') return;
    var data = {};
    Array.prototype.forEach.call(qEl.querySelectorAll('.bf__input'), function (inp) { data[inp.getAttribute('data-k')] = inp.value.trim(); });
    /* Normalise before it is stored, so every downstream consumer — the cookie,
       /submit-profile, and Fire AI's WhatsApp sender — sees one shape. */
    var dialSel = qEl.querySelector('.bf__dial');
    if (dialSel && typeof data.phone === 'string') data.phone = normalisePhone(dialSel.value, data.phone);
    state.answers[q.id] = { value: data, label: data.name || 'Details' };
  }

  /* Continue enabled only when the question has a usable answer */
  function syncContinue(q) {
    var cont = document.getElementById('bf-continue');
    if (!cont) return;
    var ok;
    if (q.type === 'fields') {
      var name = qEl.querySelector('[data-k="name"]'), email = qEl.querySelector('[data-k="email"]'), phone = qEl.querySelector('[data-k="phone"]');
      var dialSel = qEl.querySelector('.bf__dial');
      ok = name && email && phone && name.value.trim().length > 1 &&
        /.+@.+\..+/.test(email.value.trim()) &&
        phoneValid(dialSel ? dialSel.value : defaultDial(), phone.value);
    } else if (q.type === 'stepper' || q.type === 'slider') {
      ok = true; /* always has a default value */
    } else {
      ok = !!state.answers[q.id];
    }
    cont.disabled = !ok;
  }

  /* ════════════════════════════════════════════════════════════════════
     RESULT
     ════════════════════════════════════════════════════════════════════ */
  /* ---------------------------------------------------------------------
     Meta pixel -- AI Bat Fit quiz. Dataset 1380826504029254 ("zbats").
     fbq is already on the page: the Facebook & Instagram sales channel
     loads fbevents.js in the main frame (lax web-pixel sandbox), so we
     call it directly. DO NOT add a second base-pixel snippet anywhere.

     The retry exists only for the fast clicker who beats fbevents.js off
     the network. It gives up quietly after ~5s rather than throwing.
     See docs/meta-quiz-tracking.md. */
  function zbatFb(fn, tries) {
    if (typeof window.fbq === 'function') {
      try { fn(window.fbq); } catch (e) {}
      return;
    }
    tries = tries || 0;
    if (tries > 20) return;
    setTimeout(function () { zbatFb(fn, tries + 1); }, 250);
  }

  function renderResult() {
    /* fire the whole handoff (/chat + /submit-profile) behind the eval
       animation, so the reveal click has nothing left to wait for */
    /* A failed handoff used to be console-only: the reveal played out, the
       drawer opened, and the user waited for a recommendation that was never
       coming. The quiz result on screen is computed locally so it stays
       valid, but the backend match is genuinely missing — so flag it for the
       chat to own, rather than letting the silence imply everything worked.
       Still non-blocking: the user always reaches the chat either way. */
    var submitPromise = submitProfileToZac().catch(function (err) {
      if (window.console && console.warn) console.warn('Zac profile handoff failed:', err && err.message);
      var api = window.ZacAPI;
      if (api && api.state) api.state.profileFailed = true;
      /* survives the phone's navigation to /pages/zac-chat (see saveHandoff) */
      if (api && api.markHandoffFailed) api.markHandoffFailed();
      return null;
    });
    var dom = dominantAxis() || 'balance';
    var m = ARCH[dom];

    /* -- Meta Lead: the quiz is complete and contact details are captured --
       Fires here rather than on the "Reveal your match" click: that button is
       an animation gate, so a player who closes the tab during the eval
       spinner has still handed over a WhatsApp number and is still a lead. */
    (function () {
      var d = (state.answers.details && state.answers.details.value) || {};
      if (!d.phone && !d.email) return;            /* nothing to match on */

      /* One Lead per contact per page load, so Retake cannot re-fire it. */
      window.__zbatLeadSent = window.__zbatLeadSent || {};
      var key = String(d.email || d.phone).toLowerCase();
      if (window.__zbatLeadSent[key]) return;
      window.__zbatLeadSent[key] = true;

      /* commitField() has already run normalisePhone(), so d.phone is E.164
         with a leading "+" (e.g. "+919876543210"). Meta wants the digits
         without the "+", so strip non-digits and do NOT re-add a country
         code -- the dial-code picker already put the right one there. */
      var email = String(d.email || '').trim().toLowerCase();
      var phone = String(d.phone || '').replace(/\D/g, '');
      var name  = String(d.name  || '').trim().toLowerCase().split(/\s+/);
      var country = '';
      try {
        country = String((window.Shopify && window.Shopify.country) || '').toLowerCase();
      } catch (e) {}

      /* eventID is free now and saves a migration later: sending the same id
         from the Conversions API is how Meta collapses the browser and
         server copies into one Lead instead of two. */
      var eventID = 'batfit-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);

      zbatFb(function (fbq) {
        /* Advanced matching. PLAIN values on purpose -- fbq SHA-256 hashes
           them in the browser before anything is transmitted. Re-calling
           init on an already-initialised pixel updates its user data; it
           does not create a second pixel and does not fire a PageView. */
        try {
          fbq('init', '1380826504029254', {
            em: email,
            ph: phone,
            fn: name[0] || '',
            ln: name.slice(1).join(' '),
            country: country || 'in'
          });
        } catch (e) {}

        fbq('track', 'Lead', {
          content_name:     m.name,    /* "The Aggressor"  */
          content_category: m.grade,   /* "Aspire"         */
          bat_model:        m.model,   /* "6A"             */
          dominant_axis:    dom,
          lead_source:      'ai_bat_fit'
        }, { eventID: eventID });
      });
    })();

    if (topEl) topEl.innerHTML = progressDone();

    qEl.innerHTML =
      '<div class="bf__q-inner"><div class="bf__eval" id="bf-eval">' +
        '<div class="bf__eval-spin" id="bf-eval-spin" aria-hidden="true"></div>' +
        '<p class="bf__eval-eyebrow"><span class="dot" style="width:0.5rem;height:0.5rem;border-radius:50%;background:var(--gold)"></span>Zac.ai</p>' +
        '<h1 class="bf__eval-title" id="bf-eval-title">Matching you to your bat…</h1>' +
        '<p class="bf__eval-status" id="bf-eval-status" aria-live="polite">Reading your performance map…</p>' +
        '<div class="bf__eval-reveal" id="bf-eval-reveal" hidden>' +
          '<p class="bf__eval-done">Your match is ready.</p>' +
          '<button type="button" class="bf__result-cta" id="bf-reveal">Reveal your match ' + arrow() + '</button>' +
          '<button type="button" class="bf__result-retake" id="bf-result-retake">Retake AI Bat Fit Test</button>' +
        '</div>' +
      '</div></div>';

    /* archetype firms up; map locks to its matched state */
    if (archEl) archEl.innerHTML = 'archetype · <b>' + m.name + '</b>';
    updateMap();

    var statusEl = document.getElementById('bf-eval-status');
    var revealEl = document.getElementById('bf-eval-reveal');
    var spinEl   = document.getElementById('bf-eval-spin');
    var titleEl  = document.getElementById('bf-eval-title');
    var steps = [
      'Reading your performance map…',
      'Comparing 2,000+ player profiles…',
      'Weighing power, pickup & control…',
      'Locking your model…'
    ];

    function reveal() {
      if (spinEl) spinEl.hidden = true;
      if (statusEl) statusEl.hidden = true;
      if (titleEl) titleEl.hidden = true;
      if (revealEl) revealEl.hidden = false;
      /* Same reset as the welcome-back screen: wipe the saved profile+match so
         the new run scores from scratch, but keep contact details so they
         don't retype name/email/phone. */
      var retakeBtn = document.getElementById('bf-result-retake');
      if (retakeBtn) retakeBtn.addEventListener('click', function () {
        var prev = savedProfile();
        if (prev) retainedContact = { name: prev.name || '', email: prev.email || '', phone: prev.phone || '' };
        clearFitCookies();
        state.answers = {};
        state.idx = 0;
        render();
      });
      var btn = document.getElementById('bf-reveal');
      if (btn) btn.addEventListener('click', function () {
        if (btn.disabled) return;
        btn.disabled = true;
        btn.textContent = 'Find Your Best Bat Now…';
        function open() {
          if (window.ZacChat && window.ZacChat.open) window.ZacChat.open();
          else window.location.href = 'zac-chat.html?from=batfit&model=' + encodeURIComponent(m.model);
        }
        var done = false;
        function finish() { if (!done) { done = true; open(); } }
        /* The timeout guards a hung backend; it is not a race the happy path
           may lose. Firing early is harmless for the in-page drawer — it opens
           and seedConversation renders whenever the promise lands. It is NOT
           harmless when opening means navigating (phone): that tears down the
           in-flight /submit-profile and the user arrives at a chat with no
           match. So the navigating path gets the room the request needs. */
        var navigates = !(window.ZacChat && window.ZacChat.open) ||
          (window.ZacChat.wantsOwnScreen && window.ZacChat.wantsOwnScreen());
        window.setTimeout(finish, navigates ? 15000 : 4000);
        submitPromise.then(finish, finish);
      });
    }

    if (reduce) {
      if (statusEl) statusEl.textContent = steps[steps.length - 1];
      window.setTimeout(reveal, 500);
    } else {
      var i = 0;
      var iv = window.setInterval(function () {
        i += 1;
        if (i < steps.length && statusEl) statusEl.textContent = steps[i];
      }, 450);
      window.setTimeout(function () { window.clearInterval(iv); reveal(); }, 1800);
    }
  }

  function progressDone() {
    return '<div class="bf__prow"><span class="bf__prog">Complete · ' + scoredCount() + ' / ' + scoredCount() + '</span><span class="bf__state">matched</span></div>' +
      '<div class="bf__pbar"><span class="bf__pfill" style="width:100%"></span></div>';
  }

  /* ── Init ────────────────────────────────────────────────────────────── */
  drawRadarScaffold();
  render();
}());
